export const getSubdomain = () => {
    const parts: string[] = window.location.hostname
        .replace("www.", "")
        .split(".");
    // subdomain exists only if there are at least 3 parts (foo.qb.live / foo.votebox.live)
    return parts.length > 2 ? parts[0] : undefined;
};

export const getSessionJoinUrl = (codeOnline: string): string =>
    `${window.location.origin}/login/${codeOnline}`;

// Routes holding a session code (login/:onlineCode, session/:onlineCode), case-insensitive as in the Router
const SESSION_CODE_PATH_REGEXP = /^\/(?:login|session)\/([^/]+)\/*$/i;

/**
 * Session code of the route, or null when the route has none.
 * Read from the route segment rather than matched against a code pattern, so that no code escapes the subdomain check.
 */
export const getCodeOnlineFromPath = (pathname: string): string | null =>
    pathname.match(SESSION_CODE_PATH_REGEXP)?.[1] ?? null;

export interface SessionSubdomainRedirectParams {
    baseUrl: string;
    currentHostname: string;
    // Path and query string of the current page, kept by the redirection
    path: string;
    // Subdomain of the session's entity, null when the session has no entity
    sessionSubdomain: string | null;
}

/**
 * URL of the current page on the host of the session's entity (its subdomain, or the bare domain without entity), or
 * null when the participant is already on it.
 * Without this check, the subdomain of another entity would display its design for this session.
 * The domain comes from the base URL of the config, never from the current host, so that it cannot be prefixed twice.
 */
export const getSessionSubdomainRedirectUrl = ({
    baseUrl,
    currentHostname,
    path,
    sessionSubdomain,
}: SessionSubdomainRedirectParams): string | null => {
    const sessionPageUrl = new URL(path, baseUrl);
    // Hosts are case-insensitive: URL lowercases its hostname, the current one is lowercased too
    const domain = sessionPageUrl.hostname;
    const hostname = currentHostname.toLowerCase();

    // Outside of the domain (localhost), no entity design applies
    const isOnDomain = hostname === domain || hostname.endsWith(`.${domain}`);
    if (!isOnDomain) {
        return null;
    }

    if (sessionSubdomain) {
        sessionPageUrl.hostname = `${sessionSubdomain}.${domain}`;
    }

    return sessionPageUrl.hostname === hostname
        ? null
        : sessionPageUrl.toString();
};

export const getUrl = (): string => {
    const subdomain = getSubdomain();
    if (subdomain) {
        return window.env.REACT_APP_ONLINE_API_BASE_URL.replace(
            "https://",
            `https://${subdomain}.`,
        );
    } else {
        return window.env.REACT_APP_ONLINE_API_BASE_URL;
    }
};
